import { useState } from "react";
import BackArrowButton from "../backArrowButton/backArrowButton";
import styles from "./clientFiles.module.css";
import { tWithKeyAttr as t } from "../../i18nHelper";
import { useGetClientFilesQuery, useAdminDeleteClientFileMutation } from "../../services/api/user.api";
import { IGetUserResponse } from "../../services/types/users.types";
import i18n from "../../i18n";
import { getPrintDate } from "../../utils/utils";
import { noDataLabel } from "../../utils/constants";
import { BASE_URL_DYNAMIC } from "../../services/api/base-query";
import { openAuthenticatedFile } from "../../utils/fileDownload";

export type ClientFilesProps = {
  onClose?: () => void;
  user: IGetUserResponse;
};

function ClientFiles({ onClose, user }: ClientFilesProps) {
  const { data: files, isLoading, isError } = useGetClientFilesQuery(user.Guid ?? "");
  const [adminDeleteClientFile] = useAdminDeleteClientFileMutation();
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const handleDelete = async (id: number) => {
    await adminDeleteClientFile(id);
    setConfirmDeleteId(null);
  };

  const handleView = (fileName: string) => {
    openAuthenticatedFile(fileName).catch((error) =>
      console.error(t("ERROR_GENERAL"), error)
    );
  };

  return (
    <section className={styles.section} data-testid="client-files-page" dir={i18n.dir()}>
      {onClose && (
        <BackArrowButton
          text={t("PREVIOUS_SCREEN")}
          onClick={onClose}
          position={"left"}
        />
      )}
      <h3 className={styles.title}>{t("CLIENT_FILES_TITLE")}</h3>

      <div className={styles.patientInfo}>
        <h2>{t("PATIENT_DETAILS")}</h2>
        <div className={styles.patientDetails}>
          <div><strong>{t("PATIENT_NAME")}:</strong> {user?.FirstName} {user?.LastName}</div>
          <div><strong>{t("PATIENT_BIRTH_DATE")}:</strong> {user?.Birth ? getPrintDate(new Date(user.Birth)) : ""}</div>
          <div><strong>{t("PATIENT_PHONE")}:</strong> {user?.Phone || ""}</div>
          <div><strong>{t("EMAIL")}:</strong> {user?.Email || ""}</div>
        </div>
      </div>

      <div className={styles.filesList}>
        <h2>{t("CLIENT_FILES_TITLE")}</h2>
        {isLoading && <div className={styles.noFiles}>{t("LOADING")}</div>}
        {isError && <div className={styles.noFiles}>{t("ERROR_GENERAL")}</div>}
        {!isLoading && !isError && (!files || files.length === 0) && (
          <div className={styles.noFiles}>{t("CLIENT_FILES_EMPTY")}</div>
        )}
        {files && files.length > 0 && (
          <ul className={styles.list}>
            {files.map((file, index) => (
              <li key={index} className={styles.fileItem}>
                <div className={styles.fileHeader}>
                  <div className={styles.fileMeta}>
                    {file.subject && (
                      <p className={styles.metaRow}>
                        <strong>{t("CLIENT_FILES_SUBJECT")}:</strong>{" "}
                        <span>{file.subject}</span>
                      </p>
                    )}
                    {file.urgency && (
                      <p className={styles.metaRow}>
                        <strong>{t("CLIENT_FILES_URGENCY")}:</strong>{" "}
                        <span>{file.urgency}</span>
                      </p>
                    )}
                    {file.details && (
                      <p className={styles.metaRow}>
                        <strong>{t("CLIENT_FILES_DETAILS")}:</strong>{" "}
                        <span>{file.details}</span>
                      </p>
                    )}
                    {file.dateUploaded && (
                      <p className={styles.metaRow}>
                        <strong>{t("CLIENT_FILES_DATE")}:</strong>{" "}
                        <span>
                          {getPrintDate(new Date(file.dateUploaded)) || noDataLabel}
                        </span>
                      </p>
                    )}
                  </div>
                  <div className={styles.fileActions}>
                    <button
                      type="button"
                      className={styles.viewBtn}
                      onClick={() => handleView(file.fileName)}
                      title={t("CLIENT_FILES_VIEW")}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                      {t("CLIENT_FILES_VIEW")}
                    </button>
                    <a
                      href={`${BASE_URL_DYNAMIC.replace(/\/$/, "")}/${file.fileName.replace(/^\//, "")}`}
                      download
                      className={styles.downloadLink}
                      target="_blank"
                      rel="noreferrer"
                      title={t("CLIENT_FILES_DOWNLOAD")}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      {t("CLIENT_FILES_DOWNLOAD")}
                    </a>
                    {confirmDeleteId === file.id ? (
                      <div className={styles.confirmDelete}>
                        <span>{t("CONFIRM_DELETE")}</span>
                        <button className={styles.confirmBtn} onClick={() => handleDelete(file.id)}>{t("YES")}</button>
                        <button className={styles.cancelBtn} onClick={() => setConfirmDeleteId(null)}>{t("NO")}</button>
                      </div>
                    ) : (
                      <button
                        className={styles.deleteBtn}
                        onClick={() => setConfirmDeleteId(file.id)}
                        title={t("DELETE")}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                          <path d="M10 11v6M14 11v6"/>
                          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                        </svg>
                        {t("DELETE")}
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

export default ClientFiles;
